/**
 * Valid DOT hazard classes pattern.
 * Classes: 1.1-1.6, 2.1-2.3, 3, 4.1-4.3, 5.1-5.2, 6.1-6.2, 7, 8, 9
 */
export const HAZARD_CLASS = /(?:1\.[1-6]|2\.[1-3]|3|4\.[1-3]|5\.[1-2]|6\.[1-2]|[789])/;

/**
 * Strips DOT shipping details (UN/NA number, hazard class, packing group, and
 * other DOT metadata) from a waste description string.
 *
 * Examples:
 *   "UN3085, Waste Oxidizing solid, corrosive n.o.s, (SODIUM CARBONATE, DEBRIS), 5.1(8), II"
 *   → "Waste Oxidizing solid, corrosive n.o.s, (SODIUM CARBONATE, DEBRIS)"
 *
 *   "RQ, UN1993, Waste Flammable liquids, n.o.s. (Acetone, MEK), 3, PG II, ERG#128"
 *   → "Waste Flammable liquids, n.o.s. (Acetone, MEK)"
 *
 * The function removes:
 * - Leading RQ designation and UN/NA number (e.g., "RQ, UN3085, " or "NA1993, ")
 * - Trailing hazard class, packing group, and DOT metadata (e.g., ", 5.1(8), II" or ", 3, PG II, ERG#128")
 *
 * The trailing portion is stripped from the first valid DOT hazard class number
 * (preceded by a comma, period, or closing paren) through end of string.
 */
export function stripDotShippingDetails(name: string): string;
export function stripDotShippingDetails(name: string | null | undefined): string | null | undefined;
export function stripDotShippingDetails(
  name: string | null | undefined
): string | null | undefined {
  if (!name) return name;

  let result = name;

  // Strip leading "RQ, " or "RQ," prefix if present, followed by UN/NA number
  // Handles: "UN3085, ", "RQ, UN1993, ", "RQ,UN1263,", "NA1993, ", "UN1950 "
  result = result.replace(/^(?:RQ\s*,?\s*)?(?:UN|NA)\d{4}\s*,?\s*/i, "");

  // Strip trailing DOT classification from the first valid hazard class to end of string.
  // The hazard class must be preceded by a comma, period, closing paren, or space to avoid
  // matching numbers embedded in the description text.
  // Uses a lookbehind so the preceding character (especially closing parens) isn't consumed.
  // Handles subsidiary classes in parens: 5.1(8), 3(6.1)(8)
  // Handles everything after: PG II, PGIII, ERG#128, RQ(D001), NONE, etc.
  result = result.replace(
    new RegExp(`(?<=[,.)])\\s*${HAZARD_CLASS.source}(?:\\s*\\(\\d\\.?\\d?\\))*(?:[\\s,].*)?$`),
    ""
  );

  // Strip trailing commas and whitespace left after removing the classification
  result = result.replace(/[,\s]+$/, "");

  return result.trim() || name;
}
